import { graphql } from 'gatsby';
import * as React from 'react';
import { BoxDesktop, BoxMobile, ServiceBoxTitle } from '../components/Box';
import { FormButton } from '../components/Button';
import {
  ContactAddress,
  ContactAddressLi,
  ContactAddressUl,
  ContactForm,
  ContactFormHeader,
  ContactFormHeaderH2,
  ContactList
} from '../components/Contact';
import { Container } from '../components/Container';
import { FormGroup, Input, Label, Textarea } from '../components/Form';
import { GoogleMaps } from '../components/GoogleMaps';
import { SecondaryLinkExternal } from '../components/Link';
import {
  ContactContentWrapper,
  ContactSection,
  FormSection
} from '../components/Sections';
import { IDefaultPropsWithData } from '../IDefaultProps';
import { Base } from '../layouts/Base';
import { SEO } from '../utils/seo';
import { getTranslations } from '../utils/translations';

export default ({ data }: IDefaultPropsWithData) => {
  const page = data.contentfulPage!;
  const translations = getTranslations(data);
  const contactInfo = data.allContentfulContactInfo!;
  const netlifyAttr = { 'data-netlify': true };

  return (
    <Base>
      <SEO
        title={page.name as string}
        description={
          (page.preamble &&
            (page.preamble!.childContentfulRichText!.html as string)) ||
          undefined
        }
      />
      <Container>
        <ContactContentWrapper>
          <BoxMobile>
            <ServiceBoxTitle>{page.name}</ServiceBoxTitle>
          </BoxMobile>
          <ContactSection>
            {contactInfo && (
              <ContactList>
                {contactInfo.edges &&
                  contactInfo.edges.map(edge => {
                    if (!edge || !edge.node) {
                      return;
                    }
                    const { name, workTitle, phone, eMail, id } = edge.node;

                    return (
                      <ContactAddress key={id}>
                        <ContactAddressUl>
                          <ContactAddressLi> {name}</ContactAddressLi>
                          <ContactAddressLi> {workTitle}</ContactAddressLi>
                          <ContactAddressLi>
                            {translations.get("Phone")}:{' '}
                            <SecondaryLinkExternal
                              bold={true}
                              href={`tel:${phone}`}
                            >
                              +{phone}
                            </SecondaryLinkExternal>
                          </ContactAddressLi>
                          <ContactAddressLi>
                            {translations.get("Mail")}:{' '}
                            <SecondaryLinkExternal
                              bold={true}
                              href={`mailto:${eMail}`}
                            >
                              {eMail}
                            </SecondaryLinkExternal>
                          </ContactAddressLi>
                        </ContactAddressUl>
                      </ContactAddress>
                    );
                  })}
              </ContactList>
            )}

            <GoogleMaps
              // https://www.gatsbyjs.org/docs/environment-variables/#client-side-javascript
              apiKey={process.env.GATSBY_GOOGLE_MAPS_API_KEY as string}
              query={data!.contentfulCompanyInfo!.street as string}
            />
          </ContactSection>

          <FormSection>
            <BoxDesktop>
              <ServiceBoxTitle>{page.name}</ServiceBoxTitle>
            </BoxDesktop>

            <ContactForm name="contact" method="POST"  {...netlifyAttr}>
              <input type="hidden" name="form-name" value="contact" />
              <ContactFormHeader>
                <ContactFormHeaderH2>
                  {translations.get("ContactUsByMail")}:
                  {data.contentfulCompanyInfo &&
                    data.contentfulCompanyInfo.eMail && (
                      <SecondaryLinkExternal
                        href={`mailto:${data.contentfulCompanyInfo.eMail}`}
                      >
                        {data.contentfulCompanyInfo.eMail}
                      </SecondaryLinkExternal>
                    )}
                </ContactFormHeaderH2>
                <small>{translations.get("OrUseContactTemplate")}</small>
              </ContactFormHeader>
              <FormGroup>
                <Label htmlFor="name">{translations.get("Name")}</Label>
                <Input type="text" id="name" name="name"/>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="mail">{translations.get("Mail")}</Label>
                <Input type="mail" id="mail" name="mail"/>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="phone">{translations.get("Phone")}</Label>
                <Input type="phone" id="phone" name="phone"/>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="message">{translations.get("Message")}</Label>
                <Textarea id="message" rows={6} name="message"/>
              </FormGroup>
              <FormButton type="submit">{translations.get("Send")}</FormButton>
            </ContactForm>
          </FormSection>
        </ContactContentWrapper>
      </Container>
    </Base>
  );
};

export const query = graphql`
  query($id: String!, $locale: String!) {
    contentfulPage(id: { eq: $id }) {
      name
      preamble {
        childContentfulRichText {
          html
        }
      }
    }
    allContentfulContactInfo(filter: { node_locale: { eq: $locale } }) {
      edges {
        node {
          id
          name
          workTitle
          phone
          eMail
        }
      }
    }
    contentfulCompanyInfo(node_locale: { eq: "en-US" }) {
      eMail
      street
    }
    allContentfulTranslation(filter: {key: {in: ["Name", "Mail", "Message", "Phone", "Send", "ContactUsByMail", "OrUseContactTemplate"]} node_locale: {eq: $locale}}) {
      edges {
        node {
          key
          value
        }
      }
    }
  }
`;
